import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from 'antd';
import { useDocuments, usePolicyInfo } from 'all-in-one-shared-api';
import { IconCard } from 'components';
import FourItemColumn from 'components/fourItemColumn';
import { BenefitAction } from 'pages/benefits/types/benefitTypes';

import { updateActions } from '../utils/utils';
import { getLatestRenewalNumbers } from 'utils';
import { CHECK_SURGERY_CLASSIFICATION_ID } from '../constants/variables';
import SearchIcon from 'assets/images/benefits/search-icon.svg';

import { SWR_KEY } from 'pages/benefits/types/benefitTypes';
import { useKeycloakReady } from 'hooks/useKeycloakReady';

const DEFAULT_ACTIONS: BenefitAction[] = [
  {
    id: CHECK_SURGERY_CLASSIFICATION_ID,
    title: 'benefits.checkSurgeryClassification',
    description: 'benefits.checkSurgeryClassificationDescription',
    icon: SearchIcon,
    to: '/benefits/surgery-classification',
  },
];

interface BenefitActionSectionProps {
  hasSurgeryClassification?: boolean;
}

function BenefitActionSection({ hasSurgeryClassification }: BenefitActionSectionProps) {
  const { t } = useTranslation();
  const { ready, token, tokenParsed } = useKeycloakReady();

  const { shouldFetch: policyShouldFetch, data: policyInfoData } = usePolicyInfo(SWR_KEY.POLICY_INFO, token || '', {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  });

  const memberType = tokenParsed ? tokenParsed.memberType : '';

  const renewalNumbers = useMemo(() => {
    return policyInfoData ? getLatestRenewalNumbers(policyInfoData, memberType) : [];
  }, [policyInfoData, memberType]);

  const { shouldFetch: documentsShouldFetch, data: documentsData } = useDocuments(
    { name: SWR_KEY.DOCUMENTS, params: { renewalNumbers } },
    token || '',
    renewalNumbers,
  );

  const updatedActions = updateActions(
    hasSurgeryClassification ? DEFAULT_ACTIONS : [],
    memberType,
    documentsData,
    policyInfoData,
  );
  const translatedActions = updatedActions.map((action) => {
    const { title, description } = action;
    return {
      ...action,
      title: t(title),
      description: t(description),
    };
  });

  useEffect(() => {
    policyShouldFetch(ready);
    documentsShouldFetch(ready && renewalNumbers.length > 0);
  }, [renewalNumbers, policyShouldFetch, documentsShouldFetch, ready]);

  return (
    <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]} wrap>
      {translatedActions.map((action) => (
        <FourItemColumn key={action.id + Math.random()}>
          <IconCard {...action} topRightText={action.yeartitle} height="138px" />
        </FourItemColumn>
      ))}
    </Row>
  );
}

export default BenefitActionSection;
